/**
 * What the ⓘ shows when the work arrived AFTER the answer (2026-09-21).
 *
 * MILES'S ASK, verbatim: "ensure it's recorded accurately in the information i
 * icon updating it when the next components of the audio comes in etc." He had
 * pressed read aloud on his OWN message in a group thread, and the receipt was
 * wrong in three independent ways, none of which is about read aloud:
 *
 *  1. A SIDECAR STEP IS THE ONLY KIND OF WORK THAT ARRIVES LATE. A read-aloud
 *     part, a memory update, a follow-up-suggestions pass — each is rendered,
 *     priced and appended to the message minutes after its turn ended, stamped
 *     with a deterministic `sidecarKey` and `billedSeparately: true`. So the
 *     popup is not a snapshot of a finished turn; it is a list that grows while
 *     somebody is looking at it, and every rule below follows from that.
 *  2. A USER BUBBLE COULD NEVER SHOW ONE. Both popups decided "did this message
 *     need compute?" from the answer-shaped question — tokens, or a known
 *     provider, and in one of them `!isUser` outright. Reading YOUR OWN message
 *     aloud is billed to the workspace and was structurally unshowable: the
 *     bubble said "no extensive compute was necessary for this message" over a
 *     charge that had already landed.
 *  3. NINE PARTS ARE NOT NINE STEPS. A long message reads as one part after
 *     another, each its own render and its own row, so the ⓘ grew a column of
 *     near-identical "read aloud" entries and the turn's real steps scrolled
 *     away underneath them. His choice, asked and answered: ONE LINE THAT GROWS,
 *     carrying the running total and how many parts are in it.
 *
 * WHY THIS IS A MODULE AND NOT TWO EDITS. There are two ⓘ implementations —
 * `TokenInfoPopupContent` in GlobalChatSystem.tsx and the private
 * `SpecialTileTokenInfoPopup` in SpecialMessageTile.tsx — and they have drifted
 * from each other before; `orgUsagePopupLockstep.test.tsx` exists because of it,
 * and they had ALREADY drifted here (one carried `!isUser`, the other did not,
 * and only one looked past the first entry). A rule that lives in one pure place
 * and is read by both cannot drift, which is the only durable version of
 * "the two popups agree".
 *
 * Firebase-free by construction: this file imports types and the vocabulary map
 * and nothing else, so the chat-isolation battery can render either popup
 * without the load-time `firebase/auth` trap.
 */
import type { TokenUsageMetadata } from './types';
import { auxSidecarStepLabel } from '../../config/usageVocabulary';

/** The fields this module reads. Kept structural so both popups' types fit. */
export interface RollupUsage extends TokenUsageMetadata {
  cost?: { totalCost?: number } | null;
  /**
   * PRESENTATION ONLY — how many identical steps this one row stands for.
   * Never written to Firestore, never read from it: it exists on a row this
   * module SYNTHESISES and on no other.
   */
  rollupCount?: number;
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** A step the workspace paid for outside its turn: keyed, and billed on its own. */
export function isSidecarStep(u: unknown): boolean {
  if (!u || typeof u !== 'object') return false;
  const e = u as RollupUsage;
  return e.billedSeparately === true
    && typeof e.sidecarKey === 'string'
    && e.sidecarKey.trim() !== '';
}

/**
 * Did anything about this message cost work units?
 *
 * The answer-shaped half (tokens, or a provider that is not 'unknown') is
 * unchanged and is read across EVERY entry, not just the first — a multi-step
 * turn can carry its detail on a later one. The new half is the sidecar: a
 * keyed, separately-billed step means somebody paid, whoever wrote the message.
 */
export function usageShowsCompute(usages: readonly unknown[], isUser: boolean): boolean {
  const entries = (usages || []).filter((u): u is RollupUsage => !!u && typeof u === 'object');
  if (entries.some(isSidecarStep)) return true;
  if (isUser) return false;
  return entries.some((u) => (
    (u.provider !== undefined && u.provider !== null && String(u.provider) !== 'unknown')
    || num(u.inputTokens) !== undefined
    || num(u.outputTokens) !== undefined
    || num(u.reasoningTokens) !== undefined
    || num(u.citationTokens) !== undefined
    || num(u.searchQueries) !== undefined
    || num(u.totalTokens) !== undefined
    || num(u.responseTimeMs) !== undefined
  ));
}

const add = (a: number | undefined, b: number | undefined): number | undefined => (
  a === undefined && b === undefined ? undefined : (a || 0) + (b || 0));

/**
 * Collapse repeated sidecar steps that say the SAME thing into one growing row.
 *
 * Grouped by the LABEL a reader sees (`auxSidecarStepLabel`), so two kinds of
 * background work never merge into one line and an unlabelled sidecar — an
 * app-tool metering leg, which already reads clearly from its station name —
 * is left exactly where it is. Order is preserved: the group takes the position
 * of its first member, so the turn's own steps keep their numbering and the
 * background work does not jump to the end.
 *
 * Money is summed, never rounded here, and `costSource: 'estimate'` is sticky —
 * if any part of the total was priced from a published unit price rather than a
 * vendor invoice, the whole row wears the "≈" that says so.
 */
export function rollUpSidecarSteps<T extends RollupUsage>(usages: readonly T[]): Array<T | RollupUsage> {
  const list = (usages || []).filter((u): u is T => !!u && typeof u === 'object');
  const groupAt = new Map<string, number>();
  const out: Array<T | RollupUsage> = [];
  for (const u of list) {
    const label = isSidecarStep(u) ? auxSidecarStepLabel(u) : null;
    if (!label) {
      out.push(u);
      continue;
    }
    const at = groupAt.get(label);
    if (at === undefined) {
      groupAt.set(label, out.length);
      out.push({ ...u, rollupCount: 1 });
      continue;
    }
    const head = out[at] as RollupUsage;
    out[at] = {
      ...head,
      rollupCount: (head.rollupCount || 1) + 1,
      inputTokens: add(num(head.inputTokens), num(u.inputTokens)),
      outputTokens: add(num(head.outputTokens), num(u.outputTokens)),
      reasoningTokens: add(num(head.reasoningTokens), num(u.reasoningTokens)),
      totalTokens: add(num(head.totalTokens), num(u.totalTokens)),
      responseTimeMs: add(num(head.responseTimeMs), num(u.responseTimeMs)),
      cost: { ...(head.cost || {}), totalCost: add(num(head.cost?.totalCost), num(u.cost?.totalCost)) },
      costSource: head.costSource === 'estimate' || u.costSource === 'estimate' ? 'estimate' : head.costSource,
      // The newest part's key, so a later snapshot carrying the same parts
      // still dedupes against this row the way an uncollapsed one would.
      sidecarKey: u.sidecarKey,
    } as RollupUsage;
  }
  return out;
}

/**
 * The suffix a collapsed row wears: " · 3 parts". Absent for a single step, so
 * one read-aloud part reads exactly as it did before this module existed.
 */
export function rollupPartsLabel(u: unknown): string | null {
  const n = (u as RollupUsage | null)?.rollupCount;
  return typeof n === 'number' && n > 1 ? `${n} parts` : null;
}
